/* Copyright (c) 2020-present CloudIO Inc */

import { TextField, TextFieldProps, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';

import zIndex from 'constants/ZIndex';
import { isValid } from 'date-fns';

import React from 'react';
import { useUserDateFormat, useUserDateTimeFormat } from 'root/useUser';

const useStyles = makeStyles((theme: Theme) => ({
  popper: {
    zIndex: zIndex.AutoCompletePopper,
    '& .MuiDateTimePickerTabs-tabs': {
      color: '#fff',
    },
  },
}));

type Props = {
  label: string;
  value: string;
  onChange: (val: string) => void;
  showTime?: boolean;
  helperText?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  endAdornment?: React.ReactNode;
  required?: boolean;
  id?: string;
};

function DateInput(
  props: Props,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement {
  const {
    label,
    value,
    onChange,
    showTime,
    helperText,
    variant,
    endAdornment,
    required,
    id,
  } = props;
  const popperClasses = useStyles();

  const handleDateChange = (date: Date | null) => {
    if (date && isValid(date)) {
      onChange(date.toISOString());
    }
  };

  const dateFormat = useUserDateFormat();
  const dateTimeFormat = useUserDateTimeFormat();

  const dateFieldProps = {
    renderInput: (p: JSX.IntrinsicAttributes & TextFieldProps) => (
      <TextField
        {...p}
        fullWidth
        helperText={helperText}
        variant={variant}
        required={required}
        id={id}
        InputProps={{
          endAdornment: (
            <>
              {p?.InputProps?.endAdornment}
              {endAdornment}
            </>
          ),
        }}
      />
    ),
    // @ts-ignore PopperProps
    PopperProps: { className: popperClasses.popper },
    inputFormat: showTime ? dateTimeFormat : dateFormat,
    value: value || null,
    onChange: handleDateChange,
    label,
    id,
  };

  return showTime ? (
    <DateTimePicker {...dateFieldProps} />
  ) : (
    <DatePicker {...dateFieldProps} />
  );
}

export default React.forwardRef(DateInput);
